import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { catchError, of, startWith, switchMap } from 'rxjs';

import { RealtimeSocketService } from './realtime-socket.service';

import {
  REALTIME_EVENT,
  type CleaningMenuBadgePayload,
  type IssueMenuBadgeInvalidationPayload,
  type PaymentMenuBadgePayload,
  type TaskMenuBadgePayload
} from '@/shared/realtime-events';

@Injectable({ providedIn: 'root' })
export class MenuBadgeRealtimeService {
  readonly pendingCleanings = signal(0);
  readonly pendingTasks = signal(0);
  readonly pendingPayments = signal(0);
  readonly problemIssues = signal(0);

  private readonly realtimeSocketService = inject(RealtimeSocketService);
  private readonly http = inject(HttpClient);
  private started = false;

  start(includeIssueProblems = false): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.realtimeSocketService.listen<CleaningMenuBadgePayload>(REALTIME_EVENT.MENU_CLEANING_BADGES_UPDATED).subscribe({
      next: (payload) => this.pendingCleanings.set(payload.pendingCleanings),
      error: (error) => console.error('[MenuCleanigBadgeRealtimeService] Failed to receive realtime badge updates.', error)
    });
    this.realtimeSocketService.listen<TaskMenuBadgePayload>(REALTIME_EVENT.MENU_TASK_BADGES_UPDATED).subscribe({
      next: (payload) => this.pendingTasks.set(payload.pendingTasks),
      error: (error) => console.error('[MenuTaskBadgeRealtimeService] Failed to receive realtime badge updates.', error)
    });
    this.realtimeSocketService.listen<PaymentMenuBadgePayload>(REALTIME_EVENT.MENU_PAYMENT_BADGES_UPDATED).subscribe({
      next: (payload) => this.pendingPayments.set(payload.pendingPayments),
      error: (error) => console.error('[MenuPaymentBadgeRealtimeService] Failed to receive realtime badge updates.', error)
    });
    if (includeIssueProblems) {
      this.realtimeSocketService
        .listen<IssueMenuBadgeInvalidationPayload>(REALTIME_EVENT.MENU_ISSUE_BADGE_INVALIDATED)
        .pipe(
          startWith({ invalidatedAt: 0 }),
          switchMap(() =>
            this.http
              .get<{ problemIssues: number }>('/api/ext-issue/problem-count')
              .pipe(catchError(() => of({ problemIssues: this.problemIssues() })))
          )
        )
        .subscribe(({ problemIssues }) => this.problemIssues.set(problemIssues));
    }
  }

  reset(): void {
    this.pendingCleanings.set(0);
    this.pendingTasks.set(0);
    this.pendingPayments.set(0);
    this.problemIssues.set(0);
  }
}
